import axios, { HttpStatusCode } from 'axios';
import { logger } from '../log';
import { emptyFunction } from '@moniebook-web/utils';

const windowWithCsrf = globalThis as typeof globalThis & { __csrf?: string };

const CSRF_ENDPOINT = '/api/csrf';

// No explicit CSRF TTL is exposed by the BFF today, so this is a
// conservative heuristic refresh cadence rather than a value derived from
// a real backend expiry.
const PROACTIVE_REFRESH_INTERVAL_MS = 20 * 60 * 1000;
const STALE_AFTER_HIDDEN_MS = 10 * 60 * 1000;

let currentToken: string | undefined = windowWithCsrf.__csrf;
let refreshPromise: Promise<string> | null = null;

function getToken() {
    return currentToken;
}

function setToken(token: string) {
    currentToken = token;
}

async function refresh() {
    if (refreshPromise !== null) {
        return refreshPromise;
    }

    const startedAt = Date.now();

    logger.debug('csrf.refresh.started');

    refreshPromise = axios
        .get<{ token: string }>(CSRF_ENDPOINT, {
            headers: { 'x-csrf-refresh': '1' },
        })
        .then((response) => {
            setToken(response.data.token);

            logger.debug({
                eventName: 'csrf.refresh.success',
                message: `elapsedMs=${Date.now() - startedAt}`,
            });

            return response.data.token;
        })
        .catch((error: unknown) => {
            const status = axios.isAxiosError(error)
                ? error.response?.status
                : undefined;

            if (status === HttpStatusCode.Unauthorized) {
                logger.warn('csrf.session.expired');

                throw error;
            }

            logger.error({
                eventName: 'csrf.refresh.failure',
                message: `status=${status} elapsedMs=${Date.now() - startedAt}`,
            });

            throw error;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}

function handleVisibilityChange(state: { lastHiddenAt?: number }) {
    if (document.visibilityState === 'hidden') {
        state.lastHiddenAt = Date.now();

        return;
    }

    if (
        state.lastHiddenAt &&
        Date.now() - state.lastHiddenAt >= STALE_AFTER_HIDDEN_MS
    ) {
        refresh().catch(() => undefined);
    }

    state.lastHiddenAt = undefined;
}

function startProactiveRefresh(): VoidFunction {
    const intervalId = setInterval(() => {
        refresh().catch(emptyFunction);
    }, PROACTIVE_REFRESH_INTERVAL_MS);

    const state: { lastHiddenAt?: number } = {};

    const onVisibilityChange = () => handleVisibilityChange(state);

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        clearInterval(intervalId);

        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
}

export const csrfTokenManager = {
    getToken,
    setToken,
    refresh,
    startProactiveRefresh,
};
